import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormikErrors, FormikProps } from "formik";
import { fromPromise } from "mobx-utils";
import classes from "./SalesforceLoginForm.module.scss";
import parentClasses from "../../SalesforceLogin.module.scss";
import classNames from "classnames";
import * as Yup from "yup";
import { Button } from "@whatslly/ui-components/v2/Button";
import { postJSON } from "@whatslly/commons/apiUtils";
import { TERMS_CONDITIONS_TUVIS_URL, PRIVACY_POLICY_TUVIS_URL } from "@whatslly/commons/constants";
import { useDependencyInjector } from "@whatslly/commons/di";
import globalStyles from "@whatslly/ui-components/v2/globalStyles";
import { useTranslation } from "react-i18next";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { observer, useLocalObservable } from "mobx-react-lite";
import { AppIds } from "@whatslly/types/LoginProviderType";
import { FormElement } from "@whatslly/ui-components/v2/FormElements/Form";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import { FormSelect } from "@whatslly/ui-components/v2/FormElements/FormSelect";
import { FormTextInput } from "@whatslly/ui-components/v2/FormElements/FormTextInput";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import useAsyncEffect from "use-async-effect";
import type { ButtonProps } from "@whatslly/types/UI-V2/PageBuilderTypes";
import { AUTH_RESPONSE_ERROR_TYPES } from "@whatslly/types/AuthResponse";

export type EtokenExpiredType = {
  base?: string;
  logoutText?: string;
};

export type SalesForceLoginFormSettingsType = {
  customDomain?: string;
  secondButton?: ButtonProps;
  hideInputs?: boolean;
  isCustomDomain?: boolean;
  callToActionText?: string;
  connectButtonText?: string;
  hideFooter?: boolean;
  message?: React.ReactNode;
  connectButtonClassName?: string;
};

const UseCustomDomainLabel = ({ isLabel }: { isLabel?: boolean }) => {
  const { t } = useTranslation();
  return (
    <div className={classes.selectBottomTextWrapper}>
      <div className={classNames(classes.selectBottomText, globalStyles["hover-bg-color-neutral-tertiary"], { [classes.isLabel]: isLabel })}>
        <div className={classes.svgContainer}>
          <Svg iconName={`globe`} />
        </div>
        <p className={classes.textContainer}>
          {!isLabel ? (
            <>
              {t("use_custom_domain")}? <span>{t("click_here")}</span>
            </>
          ) : (
            <>{t("use_custom_domain")}</>
          )}
        </p>
      </div>
    </div>
  );
};

const SalesforceLoginForm: React.FC<{
  getOpenSalesForceTabsOptions?: () => Promise<SelectOption[]>;
  onLogin?: (navigateUrl?: string, state?: string[]) => void;
  customSettings?: SalesForceLoginFormSettingsType;
}> = observer(({ getOpenSalesForceTabsOptions, customSettings, onLogin }) => {
  const userStore = useDependencyInjector<IUserStore>(InjectUserStore);
  const [isCustomDomain, setIsCustomDomain] = useState(customSettings?.isCustomDomain ? true : false);
  const [showUserEmail, setShowUserEmail] = useState(false);
  const formikRef = useRef<FormikProps<typeof INITIAL_VALUES>>(null);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const { t } = useTranslation();
  const [loaded, setIsLoaded] = useState(getOpenSalesForceTabsOptions ? false : true);
  const [options, setOptions] = useState<SelectOption[]>([
    { label: `login.salesforce.com`, value: `login.salesforce.com` },
    { label: `test.salesforce.com`, value: `test.salesforce.com` },
    { label: <UseCustomDomainLabel />, value: ``, dontApplyOptionCss: true },
  ]);
  const [customDomainValue, setCustomDomainValue] = useState<{ value: string; label: React.ReactNode; dontApplyOptionCss?: boolean } | undefined>(
    loaded ? options[0] : { label: "", value: "" }
  );

  const INITIAL_VALUES = useMemo(
    () => ({
      email: "",
      customDomain: customSettings?.customDomain ? customSettings?.customDomain : "",
      customDomainSelect: { value: "", label: "" },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const [hideInputs, setHideInputs] = useState(customSettings?.hideInputs);

  const store = useLocalObservable(() => ({
    _login: undefined as Promise<void> | undefined,
    get login() {
      if (this._login) {
        return fromPromise(this._login);
      }
    },
    get isLoading() {
      return this.login?.state === "pending";
    },
    get error() {
      return this.login?.state === "rejected" ? (this.login?.value as any)?.toString() : undefined;
    },
    submit: async ({
      email,
      customDomain,
      customDomainSelect,
      showUserEmail,
    }: {
      email: string;
      customDomain: string;
      customDomainSelect: { value: string; label: string | React.JSX.Element; dontApplyOptionCss?: boolean };
      showUserEmail: boolean;
    }) => {
      if (store.login?.state === "pending") {
        return;
      }

      if (showUserEmail) {
        const emailError = await SalesforceLoginFormSchema()
          .validate({
            email,
          })
          .catch((e) => {
            return e.errors;
          });
        if (Array.isArray(emailError)) {
          formikRef.current?.setFieldError(`email`, emailError[0]);
          return;
        }
      }

      store._login = userStore.login({ app: AppIds.SALESFORCE, customDomain: customDomain || customDomainSelect.value, email }, t).then((result) => {
        onLogin?.(result?.navigate, result?.state);
      });
    },
  }));

  const SalesforceLoginFormSchema = useCallback(() => {
    const schema = Yup.object().shape({
      email: Yup.string()
        .email(t("auth.invalid_email"))
        .test(`Email`, t("auth.invalid_email"), async (val: string | undefined) => {
          const urlSuffix = `/sfdc/v2/auth/validate`;
          const res = await postJSON({
            urlSuffix,
            pod: import.meta.env.VITE_API_DEFAULT_POD?.toString() ?? `us1`,
            body: JSON.stringify({
              username: val || "",
              loginProvider: `Salesforce`,
              domain: formikRef.current?.values.customDomain,
            }),
          });
          const json = await res.json();
          return await json?.data?.authenticated;
        }),
    });

    return schema;
  }, [t]);

  const { error, isLoading, submit } = store;

  const validate = useCallback(
    async (values: typeof INITIAL_VALUES) => {
      const {
        email,
        customDomain,
      }: {
        email?: string;
        customDomain?: string;
      } = values;
      const errors: FormikErrors<typeof INITIAL_VALUES> = {};

      if (isCustomDomain && customDomain === "") {
        errors.customDomain = t("auth.required");
      }

      if (!isCustomDomain && (!customDomainValue || !customDomainValue?.value || customDomainValue.value === "")) {
        errors.customDomainSelect = t("auth.required") as any;
      }

      if (showUserEmail && email === "") {
        errors.email = t("auth.required");
      }
      return errors;
    },
    [customDomainValue, isCustomDomain, showUserEmail, t]
  );

  useEffect(() => {
    if (!firstLoaded) {
      return;
    }
    formikRef.current?.setFieldValue(`customDomainSelect`, customDomainValue);
    formikRef.current?.validateForm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customDomainValue]);

  useEffect(() => {
    if (!firstLoaded) {
      return;
    }
    if (!isCustomDomain) {
      formikRef.current?.setFieldValue(`customDomain`, "");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCustomDomain]);

  useEffect(() => {
    if (!firstLoaded) {
      return;
    }
    if (!showUserEmail) {
      formikRef.current?.setFieldValue(`email`, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showUserEmail]);

  useEffect(() => {
    setFirstLoaded(true);
  }, []);

  useAsyncEffect(async () => {
    if (!getOpenSalesForceTabsOptions) {
      return;
    }
    const openSalesForceTabsOptions = await getOpenSalesForceTabsOptions();
    setOptions([...openSalesForceTabsOptions, ...options]);
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded || customDomainValue?.label !== "") {
      return;
    }
    setCustomDomainValue(options[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, loaded]);

  const FormSelectMemo = useCallback(() => {
    return (
      <FormSelect
        value={customDomainValue}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options={options as any}
        menuClassName={classes.isMenu}
        placeholder={`Salesforce login URL`}
        marginBottom={`small`}
        name={`customDomainSelect`}
        onChange={(newValue: { value: string; label: React.ReactNode; dontApplyOptionCss?: boolean }) => {
          const customDomainElement = document.querySelector<HTMLInputElement>(`[name='customDomain']`);
          if (newValue.dontApplyOptionCss) {
            if (isCustomDomain && customDomainElement) {
              customDomainElement.focus();
              setIsCustomDomain(false);
              setCustomDomainValue({ label: `login.salesforce.com`, value: `login.salesforce.com` });
              return;
            }
            setCustomDomainValue({ label: <UseCustomDomainLabel isLabel />, value: `` });
            setIsCustomDomain(true);
            return;
          }
          setCustomDomainValue(newValue);
          setIsCustomDomain(false);
          return;
        }}
        paddingInline={`none`}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UseCustomDomainLabel, customDomainValue, isCustomDomain, options]);

  useEffect(() => {
    if (customSettings?.isCustomDomain) {
      setCustomDomainValue({ label: <UseCustomDomainLabel isLabel />, value: `` });
    }
  }, []);

  const errorToUse = useMemo(() => {
    if ([AUTH_RESPONSE_ERROR_TYPES.API_CURRENTLY_DISABLED, AUTH_RESPONSE_ERROR_TYPES.APEX_REST_SERVICES_DISABLED].includes(error)) {
      return <div className={classes.noApiError}>{t("salesforce_no_api")}</div>;
    }
    return (
      <div className={classes.ConnectErrorMessage} data-testid="sf-login-error">
        {error}
      </div>
    );
  }, [error, t]);

  return (
    <FormElement
      formikRef={formikRef}
      initialValues={INITIAL_VALUES}
      validate={validate}
      onSubmit={({ email, customDomain, customDomainSelect }) => {
        submit({ email, customDomain, customDomainSelect, showUserEmail });
      }}
      className={classes.SalesforceLoginForm}
    >
      {!hideInputs && (
        <div className={classes.InputsContainer}>
          <FormSelectMemo />
          {isCustomDomain && (
            <FormTextInput
              type="text"
              name={`customDomain`}
              placeholder={`https://*.my.salesforce.com or https://*.force.com/*`}
              className={classNames(classes.CustomDomain)}
              paddingInline={`none`}
              marginBottom={`small`}
            />
          )}
          {showUserEmail && (
            <FormTextInput
              type={`email`}
              name={`email`}
              data-testid={`sf-login-email`}
              placeholder={`Salesforce username (name@company.com)`}
              paddingInline={`none`}
              marginBottom={`small`}
            />
          )}
        </div>
      )}
      {customSettings?.message && customSettings.message}
      <div
        className={classNames(classes.needSalesforceUserNameContainer, parentClasses.needUser, {
          [classes.callToAction]: customSettings?.callToActionText,
        })}
      >
        {!customSettings?.callToActionText
          ? showUserEmail
            ? t("login_page.no_user_name")
            : t("login_page.need_user_name")
          : customSettings?.callToActionText}{" "}
        <span
          onClick={() => {
            if (customSettings?.hideInputs) {
              setHideInputs((prev) => !prev);
              setShowUserEmail(true);
              return;
            }
            setShowUserEmail(!showUserEmail);
          }}
          data-testid="open-need-email"
        >
          {t("click_here")}
        </span>
      </div>
      <div className={classes.ButtonsContainer}>
        <Button
          className={classNames(classes.Submit, customSettings?.connectButtonClassName)}
          text={!customSettings?.connectButtonText ? `Connect` : customSettings.connectButtonText}
          disabled={store.login?.state === "pending" || isLoading}
          data-testid={`sf-login-submit`}
          type="submit"
          isSubmitting={isLoading}
        />
        {customSettings?.secondButton && <Button type="button" {...customSettings.secondButton} />}
      </div>
      <span data-testid={"salesforce-login-error"}>{error && errorToUse}</span>
      {!customSettings?.hideFooter && (
        <div className={classNames(globalStyles["color-neutral-primary"], classes.termsAndPrivacy)}>
          {`${t("terms_privacy.processing_agree_tuvis")} `}
          <br />
          <a target="_blank" href={TERMS_CONDITIONS_TUVIS_URL} className={classNames(globalStyles["color-neutral-primary"])}>
            {t("terms_privacy.terms_of_service")}
          </a>{" "}
          {`${t("terms_privacy.and")} `}
          <a target="_blank" href={PRIVACY_POLICY_TUVIS_URL} className={classNames(globalStyles["color-neutral-primary"])}>
            {t("terms_privacy.privacy_policy")}
          </a>
        </div>
      )}
    </FormElement>
  );
});

export default SalesforceLoginForm;
